import * as React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';

import { PlatformUtils } from './shared/utils';
import { CerebroNavProvider, Urls, useApi, useAuth } from './shared/context';
import { Layout } from './shared/components/Layout';
import { CerebroBreadcrumb, CerebroPageContainer } from './shared/components';
import {
	HelloWorld,
	Introduction,
	DealIntroduction,
	DebtCapital,
	BusinessAcquisition,
	BorrowerBasicInfo,
	DebtSchedule,
	CompanyBusinessAndPrincipal,
	CompanyBorrower,
	CompanyAcquisitionTarget,
	BorrowerCollateralTypes,
	BorrowerCollateralDetails,
	ExcludedLenders,
	DealTypePreference,
	MatchingTranches,
	AcquisitionBasicInfo,
	AcquisitionCollateralTypes,
	OutputAnalysis,
	AcquisitionCollateralDetails,
	BorrowerIncomeStatement,
	AcquisitionIncomeStatement,
	Questions,
	Review,
	Results,
	DealSettings,
	Documents,
	Notes,
	Colleagues,
	RelatedDeals,
} from './pages';
import { NavigationBlockerProvider } from './shared/context/NavigationBlockerContext';
import { CerebroApiEndpoints } from './shared/utils/api';
import EmployeeRoute from './shared/components/Layout/EmployeeRoute';
import { UpdateDealStatus } from './pages/UpdateDealStatus';
import { RevisionForm } from './pages/ExistingDebt/Revision';

export const App: React.FC = () => {
	const auth = useAuth();
	const api = useApi();

	if (!auth.isAuthenticated) {
		PlatformUtils.RedirectToPlatform();
		return null;
	}

	const guid = window.location.pathname.substring(1, 37);
	if (auth.currentUser && !auth.currentUser.isEmployeeUser && guid) {
		api.post(CerebroApiEndpoints.CompanyContacts.UpdateLastAccess(guid, auth.currentUser.personId));
	}

	return !auth.isLoading ? (
		<Routes>
			<Route
				path="/"
				element={
					<NavigationBlockerProvider>
						<CerebroNavProvider>
							<Layout />
						</CerebroNavProvider>
					</NavigationBlockerProvider>
				}
			>
				<Route path="/:guid">
					<Route path={Urls.Welcome.Root} element={<Introduction />} />

					<Route path={Urls.Deal.Introduction} element={<DealIntroduction />} />
					<Route path={Urls.Deal.BusinessAcquisition} element={<BusinessAcquisition />} />
					<Route path={Urls.Deal.DebtCapital} element={<DebtCapital />} />

					<Route path={Urls.Documents.Root} element={<Documents />} />

					<Route path={Urls.Company.Borrower} element={<CompanyBorrower />} />
					<Route path={Urls.Company.BusinessAndPrincipal} element={<CompanyBusinessAndPrincipal />} />
					<Route path={Urls.Company.Acquisition} element={<CompanyAcquisitionTarget />} />

					<Route path={Urls.Debt.Root} element={<DebtSchedule />} />

					<Route path={Urls.Financials.Borrower.BasicInfo} element={<BorrowerBasicInfo />} />

					<Route path={Urls.Financials.Borrower.IncomeStatement} element={<BorrowerIncomeStatement />} />

					<Route path={Urls.Financials.Borrower.CollateralTypes} element={<BorrowerCollateralTypes />} />
					<Route path={Urls.Financials.Borrower.CollateralDetails} element={<BorrowerCollateralDetails />} />

					<Route path={Urls.Financials.Acquisition.BasicInfo} element={<AcquisitionBasicInfo />} />

					<Route
						path={Urls.Financials.Acquisition.IncomeStatement}
						element={<AcquisitionIncomeStatement />}
					/>

					<Route
						path={Urls.Financials.Acquisition.CollateralTypes}
						element={<AcquisitionCollateralTypes />}
					/>

					<Route
						path={Urls.Financials.Acquisition.CollateralDetails}
						element={<AcquisitionCollateralDetails />}
					/>

					<Route path={Urls.ExcludedLenders.Root} element={<ExcludedLenders />} />
					<Route path={Urls.Questions.Root} element={<Questions />} />

					<Route path={Urls.DealTypePreference.Root} element={<DealTypePreference />} />

					<Route path={Urls.OutputAnalysis.Root} element={<OutputAnalysis />} />
					<Route path={Urls.Colleagues.Root} element={<Colleagues />} />

					<Route element={<EmployeeRoute />}>
						<Route path={Urls.Debt.RevisionForm} element={<RevisionForm />} />
						<Route path={Urls.Matching.Root} element={<MatchingTranches />} />
						<Route path={Urls.DealSettings.Root} element={<DealSettings />} />
						<Route path={Urls.UpdateDealStatus.Root} element={<UpdateDealStatus />} />
						<Route path={Urls.Notes.Root} element={<Notes />} />
					</Route>

					<Route path={Urls.Review.Root} element={<Review />} />
					<Route path={Urls.Results.Root} element={<Results />} />
					<Route path={Urls.RelatedDeals.Root} element={<RelatedDeals />} />
					<Route
						path={Urls.Status.Root}
						element={
							<CerebroPageContainer>
								<CerebroBreadcrumb>Status</CerebroBreadcrumb>
							</CerebroPageContainer>
						}
					/>

					<Route path="HelloWorld" element={<HelloWorld />} />

					<Route index element={<Navigate to={Urls.Welcome.Root} />} />
					<Route path="*" element={<Navigate to={Urls.Welcome.Root} />} />
				</Route>
				<Route index element={<p>404</p>} />
			</Route>
		</Routes>
	) : (
		<></>
	);
};
